import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormHelperText from '@mui/material/FormHelperText'
import {
  choiceDraftFromLegacy,
  normalizeConflictingSelection,
  optionConflictsWithSelection,
  routingDestinationsFor,
  toggleMultiSelectId,
} from '../../../lib/choice-answer'
import { resolveDisplayOptions } from '../../../lib/survey-option-display'
import type { ChoiceDraftAnswer, SurveyDraftAnswer, SurveyQuestion } from '../../../lib/survey-question.types'
import {
  surveyCheckboxControlSx,
  surveyCheckboxOptionLabelSx,
  surveyCheckboxRowSelectedSx,
  surveyCheckboxRowSx,
  surveyCheckboxSelectionHintSx,
} from '../../../styles/surveyExecutionStyles'
import { ChoiceOptionLayout } from './ChoiceOptionLayout'
import { ChoiceTextSpecify } from './OtherSpecifyField'

interface MultiSelectQuestionProps {
  readonly question: SurveyQuestion
  readonly value: SurveyDraftAnswer
  readonly displaySeed: string
  readonly onChange: (value: ChoiceDraftAnswer) => void
}

export function MultiSelectQuestion({
  question,
  value,
  displaySeed,
  onChange,
}: MultiSelectQuestionProps) {
  const destinations = routingDestinationsFor(question)
  const legacyDraft = choiceDraftFromLegacy(question, value)
  const draft = {
    ...legacyDraft,
    selectedIds: normalizeConflictingSelection(legacyDraft.selectedIds, destinations),
  }
  const options = resolveDisplayOptions(question, displaySeed)
  const selectedSet = new Set(draft.selectedIds)
  const max = question.maxSelections
  const min = question.minSelections

  const toggle = (optionId: string) => {
    const nextIds = toggleMultiSelectId(draft.selectedIds, optionId, max, destinations)
    onChange({ selectedIds: nextIds, otherText: draft.otherText })
  }

  const selectionHint =
    min != null || max != null
      ? [
          min == null ? null : `Select at least ${min}`,
          max == null ? null : `at most ${max}`,
        ]
          .filter(Boolean)
          .join(', ')
      : null

  return (
    <Box>
      <ChoiceOptionLayout
        layoutHorizontal={question.layoutHorizontal}
        multiColumn={question.multiColumn}
        data-testid={`survey-q-${question.id}-options`}
      >
        {options.map((opt) => {
          const selected = selectedSet.has(opt.id)
          const atMax = max != null && draft.selectedIds.length >= max && !selected
          const conflicts =
            !selected && optionConflictsWithSelection(opt.id, draft.selectedIds, destinations)
          return (
            <Box
              key={opt.id}
              component="button"
              type="button"
              role="checkbox"
              aria-checked={selected}
              disabled={atMax || conflicts}
              data-testid={`survey-q-${question.id}-option-${opt.id}`}
              onClick={() => toggle(opt.id)}
              sx={{
                ...surveyCheckboxRowSx,
                ...(selected ? surveyCheckboxRowSelectedSx : {}),
              }}
            >
              <Checkbox
                checked={selected}
                tabIndex={-1}
                disableRipple
                sx={surveyCheckboxControlSx}
              />
              <Box component="span" sx={surveyCheckboxOptionLabelSx}>
                {opt.label}
              </Box>
            </Box>
          )
        })}
      </ChoiceOptionLayout>
      <ChoiceTextSpecify question={question} draft={draft} onChange={onChange} />
      {selectionHint ? (
        <FormHelperText sx={surveyCheckboxSelectionHintSx}>{selectionHint}</FormHelperText>
      ) : null}
    </Box>
  )
}
